import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { STATUS_LABEL, STATUS_CLASS, ORIGEM_LABEL, brDateTime, tempoRestante } from './promocoesWhitelabelUtil';
import './Reclamacoes.css';
import './PromocoesWhitelabel.css';

/**
 * Promoções de whitelabel — fila.
 *
 * O Claude, pelo MCP de QA, PROPÕE levar configs de whitelabel (empresa e fase) de QA ou HMG para
 * este ambiente. Nada muda até alguém com o tema `whitelabel_promocao` do MCP abrir a proposta
 * aqui e aprovar. A proposta vale 4 horas.
 *
 * Mesmo desenho da fila de Reclamações: sem empresa escolhida, as empresas com propostas
 * minimizadas e com a contagem; ao expandir, as propostas daquela empresa.
 */

const STATE_KEY = 'wl-promo-list-state:v1';
const DEFAULTS = { subdomain: '', status: 'pendente', open: {} };

function readState() {
    try { return { ...DEFAULTS, ...(JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}')) }; } catch (e) { return { ...DEFAULTS }; }
}
function writeState(st) {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(st)); } catch (e) { /* ignore */ }
}

function PromotionTable({ rows, onOpen, onBatch }) {
    if (!rows) return <div className="vr-empty">Carregando…</div>;
    if (rows.length === 0) return <div className="vr-empty">Nenhuma proposta com esses filtros.</div>;
    return (
        <table className="vr-table">
            <thead><tr>
                <th>Proposta</th><th>De → para</th><th>Proposta por</th><th>Criada em</th>
                <th className="vr-num">Configs</th><th>Vale na hora</th><th>Situação</th><th></th>
            </tr></thead>
            <tbody>
                {rows.map((p) => (
                    <tr key={p.id}>
                        <td>
                            <div>#{p.id}{p.batch_id && <a className="vr-chip" style={{ marginLeft: 6 }} href={`/promocoes-whitelabel-lote/${p.batch_id}`}
                                onClick={(e) => { e.preventDefault(); onBatch(p.batch_id); }}>lote</a>}</div>
                            {p.observacao && <div className="vr-muted vr-small">{p.observacao.slice(0, 90)}</div>}
                        </td>
                        <td>{ORIGEM_LABEL[p.origem] || p.origem} → {ORIGEM_LABEL[p.destino] || p.destino}</td>
                        <td className="vr-muted">{p.proposed_by_email}</td>
                        <td>
                            {brDateTime(p.created)}
                            {p.status === 'pendente' && <div className="vr-muted vr-small">{tempoRestante(p.expires_at)}</div>}
                        </td>
                        <td className="vr-num">{p.a_aplicar} de {p.itens}</td>
                        <td>
                            {p.ativos > 0
                                ? <span className="vr-chip wl-chip-live" title="Editam um config ATIVO: valem para os vendedores assim que aprovar">{p.ativos} ativo{p.ativos > 1 ? 's' : ''}</span>
                                : <span className="vr-muted vr-small">não</span>}
                        </td>
                        <td><span className={`vr-chip ${STATUS_CLASS[p.status] || ''}`}>{STATUS_LABEL[p.status] || p.status}</span></td>
                        <td className="vr-num">
                            <button className="vr-btn vr-btn-sm vr-btn-primary" onClick={() => onOpen(p.id)}>
                                {p.status === 'pendente' ? 'Revisar' : 'Ver'} ›
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function PromocoesWhitelabel() {
    const navigate = useNavigate();
    const init = readState();
    const [subdomain, setSubdomain] = useState(init.subdomain);
    const [status, setStatus] = useState(init.status);
    const [open, setOpen] = useState(init.open || {});
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => { writeState({ subdomain, status, open }); }, [subdomain, status, open]);

    const load = useCallback(async () => {
        setLoading(true);
        setRows(null);
        try {
            const r = await apiService.whitelabelPromotionsList({
                subdomain: subdomain || undefined, status: status === 'todas' ? undefined : status,
            });
            if (!r || !Array.isArray(r.propostas)) throw new Error('resposta inesperada da lista');
            setRows(r.propostas);
        } catch (e) {
            setRows([]);
            notify(e.response?.data?.detail || e.message || 'Erro ao carregar as propostas', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, status, notify]);

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const empresas = [...new Set((rows || []).map((p) => p.subdomain))].sort();
    const porEmpresa = (sd) => (rows || []).filter((p) => p.subdomain === sd);
    const openPromotion = (id) => navigate(`/promocoes-whitelabel/${id}/itens`);

    return (
        <div className="vr-page">
            {toast && <div className={`vr-toast ${toast.type}`}>{toast.msg}</div>}
            <div className="vr-list">
                <h1 className="vr-title">Promoções de whitelabel</h1>
                <p className="vr-subtitle">
                    Configs de whitelabel que o Claude propôs levar de QA ou homologação para este ambiente. Nada muda até alguém
                    com a permissão abrir a proposta, conferir as diferenças e aprovar. Cada proposta vale 4 horas.
                </p>

                <div className="vr-filters">
                    <div className="vr-field">
                        <label htmlFor="wl-empresa">Empresa</label>
                        <input id="wl-empresa" value={subdomain} placeholder="todas (ex.: leroymerlin)"
                            onChange={(e) => setSubdomain(e.target.value.trim().toLowerCase())} />
                    </div>
                    <div className="vr-field">
                        <label htmlFor="wl-status">Situação</label>
                        <select id="wl-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="pendente">Pendentes</option>
                            <option value="aplicada">Aplicadas</option>
                            <option value="recusada">Recusadas</option>
                            <option value="desfeita">Desfeitas</option>
                            <option value="expirada">Expiradas</option>
                            <option value="cancelada">Canceladas</option>
                            <option value="todas">Todas</option>
                        </select>
                    </div>
                    <div className="vr-filters-actions">
                        <button className="vr-btn vr-btn-primary" onClick={load} disabled={loading}>{loading ? 'Buscando…' : 'Buscar'}</button>
                    </div>
                </div>

                <div className="vr-companies">
                    {rows === null && <div className="vr-empty">Carregando as propostas…</div>}
                    {Array.isArray(rows) && rows.length === 0 && (
                        <div className="vr-card">
                            {status === 'pendente' ? 'Nenhuma proposta esperando decisão.' : 'Nenhuma proposta com esses filtros.'}
                        </div>
                    )}
                    {empresas.map((sd) => {
                        const lista = porEmpresa(sd);
                        const pend = lista.filter((p) => p.status === 'pendente').length;
                        return (
                            <div key={sd} className="vr-company">
                                <div className="vr-company-head" onClick={() => setOpen((p) => ({ ...p, [sd]: !p[sd] }))}>
                                    <span className={`vr-chevron ${open[sd] ? 'open' : ''}`}>›</span>
                                    <span className="vr-company-name">{sd}</span>
                                    <span className="vr-company-meta">
                                        {lista.length} proposta{lista.length > 1 ? 's' : ''} · mais recente: {brDateTime(lista[0].created)}
                                    </span>
                                    {pend > 0 && <span className="vr-count" title="pendentes">{pend}</span>}
                                </div>
                                {open[sd] && <PromotionTable rows={lista} onOpen={openPromotion} onBatch={(b) => navigate(`/promocoes-whitelabel-lote/${b}`)} />}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default PromocoesWhitelabel;
